import { get } from "@ember/object"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookSearch } from "@shared/models/analytics/planbook"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import _ from "lodash"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "@shared/actions/create-narrative"
import createNarrative from "@shared/actions/create-narrative"

let defaultRelatedModels = function (findFn, payload, actionId, session) {
  return {
    session,
    actionId,
  }
}

let defaultContext = function (payload, models) {
  // We use get as some of these values might be null/undefined and we want to not break
  return {
    sessionId: get(models, "session.id"),
    userId: get(models, "session.userId"),
    sequenceInSession: get(models, "session.sequenceNumber"),
    secondsSinceLastAction: get(models, "session.secondsSinceLastAction"),
  }
}

let genericParams = {
  componentId: "string",
  sessionId: "string",
  attrs: "object",
}

let genericPatches = function patches(payload) {
  let updater = Patch.create("app-state", payload.sessionId)
  _.each(payload.attrs, (val, key) => {
    updater.set(`attributes.${payload.componentId}.${key}`, val)
  })
  return [updater.patch]
}

let genericNarrative = function (titleForAnalytics) {
  return function (_payload, _findFn, _userId) {
    let payload = _payload || {}
    return {
      title: payload.title || null,
      titleForAnalytics: titleForAnalytics,
      description: payload.description || null,
      context: payload.context || {},
      activity: null,
    }
  }
}

let actions = {
  REGISTER_COMPONENT: {
    params: {
      sessionId: "string",
      componentId: "string",
      defaultState: "object",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId,
        payload.defaultState
      ).patch
      return [patch]
    },
    undoPatches: null,
    narrative: null,
  },

  DEREGISTER_COMPONENT: {
    params: {
      sessionId: "string",
      componentId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId,
        1
      ).patch
      return [patch]
    },
    undoPatches: null,
    narrative: null,
  },

  LESSON_STANDARDS_MODAL_CLOSE: {
    params: {
      sessionId: "string",
      componentId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".isShowingModal",
        false
      ).patch
      return [patch]
    },
    undoPatches: null,
    narrative(payload) {
      let models = defaultRelatedModels

      return createNarrative({
        titleForAnalytics: "Standards - Close modal",
        context: defaultContext,
      })
    },
  },

  CLOSE_LESSON_TEMPLATE_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".isOpen",
        false
      ).patch
      return [patch]
    },
  },

  CLOSE_UNIT_COMPARISON_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".isOpen",
        false
      ).patch
      return [patch]
    },
    narrative(payload) {
      let models = defaultRelatedModels

      return createNarrative({
        titleForAnalytics: "Unit - Comparison Modal - Close",
        context: defaultContext,
      })
    },
  },

  SET_CLASS_WEBSITE_SLUG_VALIDATION: {
    params: {
      componentId: "string",
      sessionId: "string",
      slug: { type: "string", optional: true },
      isValid: "boolean",
      message: { type: "string", optional: true },
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId)
        .set(`attributes.${payload.componentId}.slugIsValid`, payload.isValid)
        .set(`attributes.${payload.componentId}.slugErrorMessage`, payload.message).patch
      return [patch]
    },
  },

  OPEN_PRINT_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
      perspective: "string",
      startDate: "string",
      endDate: "string",
      medium: "string",
      currentUserId: "string",
      courseIds: "array",
      courseId: "string",
      planbookId: "string",
      dateRangeString: "string",
      canPrint: "boolean",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId)
      _.chain(payload)
        .map((val, key) => [`attributes.${payload.componentId}.${key}`, val])
        .forEach((tuple) => patch.push(tuple[0], tuple[1]))
        .value()

      patch.push(`attributes.${payload.componentId}.isOpen`, true).patch

      return [patch]
    },
    narrative(payload) {
      let models = defaultRelatedModels
      // eslint-disable-next-line ember/no-string-prototype-extensions
      let title = _.capitalize(`${payload.medium} ${payload.perspective}`)

      return createNarrative({
        titleForAnalytics: title,
        context: defaultContext,
      })
    },
  },
  CLOSE_PRINT_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".isOpen",
        false
      ).patch
      return [patch]
    },
  },

  CLOSE_PAYWALL_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".isOpen",
        false
      ).patch
      return [patch]
    },
  },

  OPEN_COURSE_OUTLINE_MODAL: {
    params: {
      componentId: "string",
      sessionId: "string",
      courseId: "string",
      focusedItemId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId)
        .push("attributes." + payload.componentId + ".isOpen", true)
        .push("attributes." + payload.componentId + ".courseId", payload.courseId)
        .push("attributes." + payload.componentId + ".focusedItemId", payload.focusedItemId).patch
      return [patch]
    },
    narrative(payload) {
      let models = defaultRelatedModels

      return createNarrative({
        titleForAnalytics: "Unit Planner - Show Outline",
        context: defaultContext,
      })
    },
  },

  SIDEBAR_SET_UNIT_ID: {
    params: {
      componentId: "string",
      sessionId: "string",
      cardStackId: "string",
      unitInfo: "object",
      courseId: "string",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId)
        .push("attributes." + payload.componentId + ".cardStackId", payload.cardStackId)
        .push("attributes." + payload.componentId + ".unitInfo", payload.unitInfo)
        .push("attributes." + payload.componentId + ".courseId", payload.courseId)
        .push("attributes." + payload.componentId + ".isUnit", true).patch
      return [patch]
    },
  },

  SIDEBAR_OPEN: {
    params: {
      componentId: "string",
      sessionId: "string",
      cardStackId: "string",
      unitInfo: "object",
    },
    patches(payload) {
      let patch = Patch.create("app-state", payload.sessionId).push(
        "attributes." + payload.componentId + ".showSidebar",
        true
      ).patch
      return [patch]
    },
    narrative(payload) {
      let models = defaultRelatedModels

      return createNarrative({
        titleForAnalytics: "Lesson Sidebar - Open",
        context: defaultContext,
      })
    },
  },

  PLANBOOK_SEARCH_BOX_UNFOCUS: {
    params: { query: "string" },
    patches: null,
    undoPatches: null,
    narrative(payload, findFn: FindFn, userId): Narrative {
      let newEvent: PlanbookSearch = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_id: payload.planbookId,
        primary_object_type: "planbook",
        primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "search",
        primary_object_specifier: null,
        adverbial_verb: null,
        adverbial_object_type: null,
      }
      return createNarrative({
        titleForAnalytics: "Planbook - Search - Blur Search Box",
        context: {
          searchTerm: payload.query,
        },
        newAnalyticsEvents: [newEvent],
      })
    },
  },

  PLANBOOK_SEARCH_SELECT_RESULT: {
    params: {},
    patches: null,
    undoPatches: null,
    narrative(payload, findFn: FindFn, userId): Narrative {
      let newEvent: PlanbookSearch = {
        ...createBlankEvent(),
        root_id: payload.planbookId,
        root_type: "planbook",
        primary_object_type: "planbook",
        primary_object_id: payload.planbookId,
        primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),
        primary_object_id_and_ancestor_ids: [payload.planbookId],
        primary_verb: "search",
        primary_object_specifier: null,
        adverbial_verb: "selecting",
        adverbial_object_type: "lesson",
        adverbial_object_id: payload.resultId,
      }
      return createNarrative({
        titleForAnalytics: "Planbook - Search - Select Result",
        newAnalyticsEvents: [newEvent],
      })
    },
  },
}

export const MARKETING_CLICK_FEATURE = {
  name: "MARKETING_CLICK_FEATURE",
  patches: null,
  narrative: genericNarrative("Marketing - Click Feature"),
}

export const PAGE_VISIT = {
  name: "PAGE_VISIT",
  patches: null,
  undoPatches: null,
  narrative: genericNarrative("Page - Visit"),
}

export const LESSON_STANDARDS_MODAL_OPEN = {
  name: "LESSON_STANDARDS_MODAL_OPEN",
  params: {
    sessionId: "string",
    componentId: "string",
  },
  patches(payload) {
    let patch = Patch.create("app-state", payload.sessionId).push(
      "attributes." + payload.componentId + ".isShowingModal",
      true
    ).patch
    return [patch]
  },
  narrative(payload) {
    let models = defaultRelatedModels

    return createNarrative({
      titleForAnalytics: "Standards - Open modal",
      context: defaultContext,
    })
  },
}

export default actions
